import i18next from 'i18next';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { useApp } from '@egr/xbox/base-app/AppContext';
import { toLocaleDateString } from '@egr/xbox/utils/Date';

import { isNotNullOrEmpty } from '@easterngraphics/wcf/modules/utils/string';

interface LanguageContextProps {
    dataLanguage: string | undefined;
    guiLanguage: string;
    toLocaleDateString: (value: Date | null | undefined) => string | null;
}

export function languageFromI18next() {
    return i18next.language?.split('-')[0] ?? 'en';
}

const LanguageContextDefault: LanguageContextProps = {
    dataLanguage: undefined,
    guiLanguage: i18next.language?.split('-')[0] ?? 'en',
    toLocaleDateString: (value: Date | null | undefined) => { return value?.toLocaleDateString() ?? null; },
};

export const LanguageContext: React.Context<LanguageContextProps> =
    React.createContext<LanguageContextProps>(LanguageContextDefault);

export const LanguageProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
    const app = useApp();
    const { i18n } = useTranslation();
    const [ internalDataLanguage, setDataLanguage] = React.useState<string | null | undefined>();

    const updateLanguage: () => Promise<void> = React.useCallback(
        async () => {
            if (app.eaiwsBaskets?.session?.isValid ?? false) {
                const nextLanguage: string | null | undefined = app.sessionManager.getLastSetPrimaryLanguage();
                if (
                    isNotNullOrEmpty(nextLanguage) &&
                    !i18n.languages.includes(nextLanguage)
                ) {
                    await i18n.loadLanguages([nextLanguage]);
                }
                setDataLanguage(nextLanguage);
            }
        },
        [
            i18n,
        ]
    );

    React.useEffect(
        () => {
            void updateLanguage();
            app.sessionManager.onChange.addListener(updateLanguage);

            return () => { app.sessionManager.onChange.removeListener(updateLanguage); };
        },
        []
    );

    const i18nLanguage = i18n.language.split('-')[0] ?? 'de';

    return (
        <LanguageContext.Provider
            value={{
                dataLanguage: internalDataLanguage ?? i18nLanguage,
                guiLanguage: i18nLanguage,
                toLocaleDateString: (value) => {
                    return (value && toLocaleDateString(value, i18n.language)) ?? null;
                }
            }}
        >
            {children}
        </LanguageContext.Provider>
    );
};

LanguageProvider.displayName = 'LanguageProvider';